import {
  ContactFieldValueInterface,
  ContactSlugEnum,
  FormInput,
  FormInputPhone,
  Modal,
  PrimaryButton,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import {
  ManageFieldsEditErrorResponseInterface,
  ManageFieldsInterface,
} from 'modules/contacts/components/update-fields-modal/types/manage-fields-interface'
import UpdateFieldsModalSkeleton from 'modules/contacts/components/update-fields-modal/update-fields-modal-skeleton'
import React, { ChangeEvent, useEffect } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { KeyedMutator } from 'swr'
import { SaveIcon } from '@systemeio/ui/icons'

interface UpdateFieldsModalProps {
  opened: boolean
  data: ManageFieldsInterface | undefined
  onClose: () => void
  title: string
  isLoadingFields: boolean
  isFetching: boolean
  updateFields: (data: ManageFieldsInterface) => Promise<void>
  mutate: KeyedMutator<ManageFieldsInterface>
  unusedFields?: (
    data: ManageFieldsInterface | undefined,
  ) => undefined | { id: number; fieldValues: ContactFieldValueInterface[] }
}

function UpdateFieldsModal({
  opened,
  onClose,
  isLoadingFields,
  isFetching,
  title,
  updateFields,
  mutate,
  data,
  unusedFields,
}: UpdateFieldsModalProps) {
  const { t } = useLocoTranslation()

  const [tempState, setTempState] = React.useState<ManageFieldsInterface | undefined>()
  const [errors, setErrors] = React.useState<ManageFieldsEditErrorResponseInterface>()

  useEffect(() => {
    if (data && unusedFields) {
      setTempState(unusedFields(data))
    }
  }, [data])

  function onChangeField(e: ChangeEvent<HTMLInputElement> | string, index: number) {
    const value = typeof e === 'string' ? e : e.target.value

    setTempState(prev => {
      if (!prev) return prev

      const updatedFieldValues = [...prev.fieldValues]

      updatedFieldValues[index] = { ...updatedFieldValues[index], value }

      return { ...prev, fieldValues: updatedFieldValues }
    })
  }

  async function onSubmit() {
    try {
      if (tempState) {
        setErrors(undefined)
        await updateFields(tempState)
        await mutate(tempState)
        onClose()
        toast.success(t('global.changes_saved'))
      }
    } catch (error) {
      if (error instanceof BadRequest) {
        setErrors(error.errors)
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      isFetching={isFetching}
      contentClassName={'flex flex-col gap-7'}
    >
      {isLoadingFields ? (
        <UpdateFieldsModalSkeleton />
      ) : (
        tempState?.fieldValues.map((item, index) => {
          const Input =
            item.contactField.slug === ContactSlugEnum.PhoneNumber ? FormInputPhone : FormInput

          return (
            <Input
              key={`${item.contactField.slug}-${index}`}
              label={item.contactField.fieldName}
              value={item.value || ''}
              onChange={(e: ChangeEvent<HTMLInputElement> | string) => onChangeField(e, index)}
              error={errors?.fields?.fields[item.contactField.slug]?.join('\n')}
            />
          )
        })
      )}
      <PrimaryButton
        width={'large'}
        className={'mx-auto w-fit'}
        isFetching={isFetching || isLoadingFields}
        disabled={isFetching}
        onClick={onSubmit}
      >
        <SaveIcon />
        {t('global.save')}
      </PrimaryButton>
    </Modal>
  )
}

export default UpdateFieldsModal
